/// <amd-module name="Test/TestPlansHubRefresh/TestCasesClonePanel/Component/TestCasesClonePanelConnectedView" />

import * as React from "react";
import * as Resources from "Test/TestPlansHubRefresh/TestCasesClonePanel/Resources";

import { Components, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { CustomHeader, HeaderTitleArea } from "VSSUI/Header";
import { CustomPanel, PanelCloseButton, PanelContent, PanelFooter } from "VSSUI/Panel";
import { Dropdown, DropdownExpandableButton } from "VSSUI/Dropdown";
import { IListBoxItem, ListBoxItemType } from "VSSUI/ListBox";
import { ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { TeamProject, TeamProjectReference } from "Tfs/Platform/Contracts/Core";
import { TestPlan, TestSuite, TestSuiteType } from "TestPlanCommon/Contracts/TestPlan";
import { TestHubRefreshConstants } from "Test/TestPlansHubRefresh/TestHub/Constants";
import { EditableDropdown } from "VSSUI/EditableDropdown";
import { Icon, IconSize } from "VSSUI/Icon";
import { css } from "VSSUI/Util";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { TestHubRefreshLocalContext } from "Test/TestPlansHubRefresh/TestHub/Utils/TestPlansHubRefreshLocalContextProvider";
import { Button } from "VSSUI/Button";
import { Checkbox } from "VSSUI/Checkbox";
import { Dispatch } from "redux";
import { DynamicModuleLoader } from "redux-dynamic-modules";
import { FormItem } from "VSSUI/FormItem";
import { ITestCasesClonePanelAwareState } from "Test/TestPlansHubRefresh/TestCasesClonePanel/Redux/TestCasesClonePanelContracts";
import { ITfsPageService } from "Tfs/Platform/Page";
import { IVssComponentContext } from "VSS/Platform/Layout";
import { IVssPageContext } from "VSS/Platform/Context";
import { ListSelection } from "VSSUI/List";
import { TestCasesClonePanelActions } from "Test/TestPlansHubRefresh/TestCasesClonePanel/Redux/TestCasesClonePanelActions";
import { TestCasesClonePanelModule } from "Test/TestPlansHubRefresh/TestCasesClonePanel/Redux/TestCasesClonePanelModule";
import { connect } from "react-redux";
import { TelemetryConstants } from "Test/TestPlansHubRefresh/TestHub/Constants";
import { TelemetryActions } from "Test/TestPlansHubRefresh/TestHub/Redux/TelemetryActions";

export interface ITestCaseClonePanelOwnProps extends IVssComponentProperties {
    showPanel: ObservableValue<boolean>;
    sourceSuiteId: number;
    sourcePlanId: number;
    sourcePlanName: string;
    sourceTestCaseIds: number[];
}

export interface ITestCaseClonePanelViewProps {
    cloneInProgress: boolean; // if clone is in progress
    opId: number | undefined; // opId associated with the new clone
    mandatoryFieldUpdated: boolean; // if all the mandatory fields in the Panel are updated
    projectList: TeamProjectReference[];
    testPlanList: TestPlan[];
    testPlanListProject: string;
    suiteList: TestSuite[];
    testPlanSelectionErrorMessage: string;
    testSuiteSelectionErrorMessage: string;
    disableTestSuiteDropdown: boolean;
}

export interface ITestCaseClonePanelViewDispatchProps {
    updateMandatoryField: (update: boolean) => void; // dispatch updateMandatoryField action
    getProjectsFromServer: () => void;
    getTestPlans: (projectName: string) => void;
    getTestSuitesForPlan: (project: string, planId: number) => void;
    copyTestCases: (
        sourcePlanId: number,
        sourcePlanName: string,
        destinationPlanId: number,
        destinationPlanName: string,
        sourceSuiteId: number,
        destinationSuiteId: number,
        sourceProjectName: string,
        destinationProjectName: string,
        testCasesIds: number[],
        includeLinks: boolean,
        includeAttachments: boolean
    ) => void;
    testCasesClonePanelError: (testPlanSelectionErrorMessage: string) => void;
    publishTelemetry(action: string, payload: { [key: string]: any }): void;
    testCasesClonePanelSuiteSelectionError: (testSuiteSelectionErrorMessage: string) => void;
    disableTestSuiteEditableDropdownSelection: (disableTestSuiteDropdown: boolean) => void;
}

export class TestCasesClonePanelView extends VssComponent<
    ITestCaseClonePanelOwnProps & ITestCaseClonePanelViewProps & ITestCaseClonePanelViewDispatchProps
> {
    private _sourceProjectName: string = "";
    private _selectedProjectName: string = "";
    private _selectedTestPlanName: string = "";
    private _SelectedTestPlanId: number = -1;
    private _selectedSuiteName: string = "";
    private _selectedSuiteId: number = -1;
    private _projectListFetched: boolean = false;
    private _projectList: ObservableArray<IListBoxItem<{}>>;
    private _testPlanListFetchedForProject: string = "";
    private _testPlanObjectList: TestPlan[];
    private _lastTestPlanListFetched: TestPlan[];
    private _testPlanList: ObservableArray<IListBoxItem<{}>>;
    private _suiteObjectList: TestSuite[];
    private _suiteList: ObservableArray<IListBoxItem<{}>>;
    private _projectSelectionMethod: ListSelection;
    private _testPlanSelectionMethod: ListSelection;
    private _suiteSelectionMethod: ListSelection;
    private _projectsLoading: ObservableValue<boolean>;
    private _testPlansLoading: ObservableValue<boolean>;
    private _suitesLoading: ObservableValue<boolean>;
    private _includeAttachments: ObservableValue<boolean>;
    private _includeLinks: ObservableValue<boolean>;
    private _selectedTestPlanNameObservable: ObservableValue<string>;
    private _selectedTestSuiteNameObservable: ObservableValue<string>;
    private _isEditableDropdownEnabled: boolean;

    constructor(
        props: ITestCaseClonePanelOwnProps & ITestCaseClonePanelViewProps & ITestCaseClonePanelViewDispatchProps,
        context?: IVssComponentContext
    ) {
        super(props);
        const pageContext: IVssPageContext = context!.pageContext;
        const tfsPageService = pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();

        if (tfsPageData && tfsPageData.project) {
            this._sourceProjectName = tfsPageData.project.name;
            this._selectedProjectName = tfsPageData.project.name;
        }

        this._projectSelectionMethod = new ListSelection({ multiSelect: false, selectOnFocus: false });
        this._testPlanSelectionMethod = new ListSelection({ multiSelect: false, selectOnFocus: false });
        this._suiteSelectionMethod = new ListSelection({ multiSelect: false, selectOnFocus: false });

        this._projectsLoading = new ObservableValue<boolean>(false);
        this._testPlansLoading = new ObservableValue<boolean>(false);
        this._suitesLoading = new ObservableValue<boolean>(false);
        this._includeAttachments = new ObservableValue<boolean>(false);
        this._includeLinks = new ObservableValue<boolean>(false);

        this._projectList = new ObservableArray<IListBoxItem<{}>>();
        this._testPlanList = new ObservableArray<IListBoxItem<{}>>();
        this._suiteObjectList = [];
        this._suiteList = new ObservableArray<IListBoxItem<{}>>();
        this._selectedTestPlanNameObservable = new ObservableValue<string>("");
        this._selectedTestSuiteNameObservable = new ObservableValue<string>("");

        this._isEditableDropdownEnabled = isFeatureFlagEnabled(
            pageContext,
            TestHubRefreshConstants.EnableEditableDropdownForImportingTestSuiteAndCopyingTestCasesFF,
            false
        );
    }

    private isValidId(id: string) {
        // Regular expression to match only digits
        const digitRegex = /^\d+$/;
        const validId = digitRegex.test(id);

        return validId;
    }

    private validTestSuite(item: any) {
        const validSuiteId = this.isValidId(item.id);
        if (validSuiteId) {
            this.props.testSuiteSelectionErrorMessage && this.props.testCasesClonePanelSuiteSelectionError("");

            //Publish telemetry for test suite selection
            const telemetryPayload = {
                suiteId: item.id
            };

            //The item.id and item.text will be equal when the test suite ID is entered in the input field, rather than being selected from the dropdown menu.
            if (item.id == item.text) {
                this.props.publishTelemetry(TelemetryConstants.COPYTESTCASES_TESTSUITE_ID_SEARCH, telemetryPayload);
            } else {
                this.props.publishTelemetry(TelemetryConstants.COPYTESTCASES_TESTSUITE_DROPDOWN_SELECTION, telemetryPayload);
            }
        }
        else {
            this.props.testCasesClonePanelSuiteSelectionError(Resources.ValidTestSuiteIdMessage);

            //Publish telemetry for test suite selection error
            const telemetryPayload = {
                suiteId: item.id,
                error: Resources.ValidTestSuiteIdMessage
            };

            this.props.publishTelemetry(TelemetryConstants.COPYTESTCASES_TESTSUITE_SELECTION_ERROR, telemetryPayload);
        }
        return validSuiteId;
    }

    private validateTestPlan(item: any) {
        const validTestPlanId = this.isValidId(item.id);
        if (validTestPlanId) {
            this.props.testPlanSelectionErrorMessage && this.props.testCasesClonePanelError("");

            const telemetryPayload = {
                planId: item.id
            };

            //The item.id and item.text will be equal when the test plan ID is entered in the input field, rather than being selected from the dropdown menu.
            if (item.id == item.text) {
                this.props.publishTelemetry(TelemetryConstants.COPYTESTCASES_TESTPLAN_ID_SEARCH, telemetryPayload);
            } else {
                this.props.publishTelemetry(TelemetryConstants.COPYTESTCASES_TESTPLAN_DROPDOWN_SELECTION, telemetryPayload);
            }

            return true;
        } else {
            this.props.testCasesClonePanelError(Resources.ValidTestPlanIdMessage);
            this.props.disableTestSuiteEditableDropdownSelection(true);

            //Publish telemetry for test plan selection error
            const telemetryPayload = {
                planId: item.id,
                error: Resources.ValidTestPlanIdMessage
            };

            this.props.publishTelemetry(TelemetryConstants.COPYTESTCASES_TESTPLAN_SELECTION_ERROR, telemetryPayload);
            return false;
        }
    }

    public render(): JSX.Element {
        let panelContent = (
            <div className="test-cases-clone-panel-content">
                <FormItem
                    label={this._isEditableDropdownEnabled ? Resources.SelectProjectLabel : Resources.SelectProject}
                    className="test-project-name"
                >
                    <Dropdown
                        className="project-dropdown-button"
                        ariaLabel={this._selectedProjectName}
                        items={this._projectList}
                        selection={this._projectSelectionMethod}
                        loading={this._projectsLoading}
                        onSelect={(event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem<{}>) => {
                            // Refactor common code
                            if (item.text && item.text != this._selectedProjectName) {
                                this._testPlanList.removeAll();
                                this._isEditableDropdownEnabled
                                    ? (this._selectedTestPlanNameObservable.value = "")
                                    : (this._selectedTestPlanName = "");

                                this._isEditableDropdownEnabled &&
                                    this.props.testPlanSelectionErrorMessage &&
                                    this.props.testCasesClonePanelError("");

                                this._suiteList.removeAll();
                                this._isEditableDropdownEnabled ? (this._selectedTestSuiteNameObservable.value = "") : (this._selectedSuiteName = "");
                                this._isEditableDropdownEnabled &&
                                    this.props.testSuiteSelectionErrorMessage &&
                                    this.props.testSuiteImportPanelSuiteSelectionError("");
                                this.props.disableTestSuiteEditableDropdownSelection(true);

                                this._selectedProjectName = item.text;
                                this._testPlansLoading.value = true;
                                this._testPlanList.push({ id: "loading", type: ListBoxItemType.Loading });
                                this.props.getTestPlans(item.text);
                            }
                        }}
                        placeholder={this._selectedProjectName}
                        noItemsText={Resources.NoProjects}
                        calloutContentClassName={""}
                    />
                </FormItem>

                {this._isEditableDropdownEnabled ? (
                    <FormItem label={""} className="test-plan-name">
                        <div className={css("inline-flex-row", "body-m", "margin-bottom-4")}>
                            <label data-tip="Additional information">{Resources.TestPlanLabel}</label>
                            <Icon
                                ariaLabel={Resources.SelectTestPlanInfo}
                                iconName="Info"
                                className={css("margin-left-4")}
                                size={IconSize.medium}
                                tooltipProps={{ text: Resources.SelectTestPlanInfo }}
                            />
                            {this.props.testPlanSelectionErrorMessage && (
                                <span data-tip="error" className={css("error-text", "margin-left-4")}>
                                    {this.props.testPlanSelectionErrorMessage}
                                </span>
                            )}
                        </div>

                        <EditableDropdown
                            allowFreeform={true}
                            allowTextSelection={true}
                            ariaLabel={
                                this._selectedTestPlanNameObservable.value ? this._selectedTestPlanNameObservable.value : Resources.SelectTestPlanInfo
                            }
                            className={"plan-dropdown-button"}
                            placeholder={Resources.SelectTestPlanOrEnterTestPlanId}
                            disabled={false}
                            items={this._testPlanList}
                            onValueChange={(item: IListBoxItem<{}>) => {
                                if (item && item.id && item.text && item.text != this._selectedTestPlanNameObservable.value) {
                                    this.props.testSuiteSelectionErrorMessage && this.props.testCasesClonePanelSuiteSelectionError("");
                                    this._suiteList.removeAll();
                                    this._selectedTestSuiteNameObservable.value = "";
                                    this._selectedSuiteId = -1;

                                    this._selectedTestPlanNameObservable.value = item.text;

                                    if (this.validateTestPlan(item)) {
                                        this._SelectedTestPlanId = parseInt(item.id, 10);
                                        this._suitesLoading.value = true;
                                        this._suiteList.push({ id: "loading", type: ListBoxItemType.Loading });
                                        this.props.getTestSuitesForPlan(this._selectedProjectName, parseInt(item.id, 10));
                                        this.props.disableTestSuiteEditableDropdownSelection(false);
                                    }
                                    this.enableCreateButtonIfRequired();
                                }
                            }}
                            selection={this._testPlanSelectionMethod}
                            selectedText={this._selectedTestPlanNameObservable}
                            noItemsText={Resources.NoTestPlans}
                            calloutContentClassName={""}
                        />
                    </FormItem>
                ) : (
                    <FormItem label={Resources.SelectTestPlan} className="test-plan-name">
                        <Dropdown
                            className="plan-dropdown-button"
                            ariaLabel={this._selectedTestPlanName}
                            items={this._testPlanList}
                            selection={this._testPlanSelectionMethod}
                            loading={this._testPlansLoading}
                            onSelect={(event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem<{}>) => {
                                if (item.text && item.id && item.text != this._selectedTestPlanName) {
                                    this._suiteList.removeAll();
                                    this._selectedSuiteName = "";
                                    this._selectedSuiteId = -1;
                                    this.enableCreateButtonIfRequired();

                                    this._selectedTestPlanName = item.text;
                                    this._SelectedTestPlanId = parseInt(item.id, 10);
                                    this._suitesLoading.value = true;
                                    this._suiteList.push({ id: "loading", type: ListBoxItemType.Loading });
                                    this.props.getTestSuitesForPlan(this._selectedProjectName, parseInt(item.id, 10));
                                }
                            }}
                            noItemsText={Resources.NoTestPlans}
                            calloutContentClassName={""}
                        />
                    </FormItem>
                )}

                {this._isEditableDropdownEnabled ? (
                    <FormItem label={""} className="test-suite-name">
                        <div className={css("inline-flex-row", "body-m", "margin-bottom-4")}>
                            <label data-tip="Additional information">{Resources.TestSuiteLabel}</label>
                            <Icon
                                ariaLabel={Resources.SelectTestSuiteInfo}
                                iconName="Info"
                                className={css("margin-left-4")}
                                size={IconSize.medium}
                                tooltipProps={{ text: Resources.SelectTestSuiteInfo }}
                            />
                            {this.props.testSuiteSelectionErrorMessage && (
                                <span data-tip="error" className={css("error-text", "margin-left-4")}>
                                    {this.props.testSuiteSelectionErrorMessage}
                                </span>
                            )}
                        </div>
                        <EditableDropdown
                            allowFreeform={true}
                            allowTextSelection={true}
                            ariaLabel={
                                this._selectedTestSuiteNameObservable.value
                                    ? this._selectedTestSuiteNameObservable.value
                                    : Resources.SelectTestSuiteInfo
                            }
                            className={"suite-dropdown-button"}
                            placeholder={Resources.SelectTestSuiteOrEnterTestSuiteId}
                            disabled={this.props.disableTestSuiteDropdown}
                            items={this._suiteList}
                            onValueChange={(item: IListBoxItem<{}>) => {
                                if (item && item.text && item.text != this._selectedTestSuiteNameObservable.value) {
                                    if (this.validTestSuite(item)) {
                                        this._selectedTestSuiteNameObservable.value = item.text;
                                        this._selectedSuiteId = item.id ? parseInt(item.id, 10) : -1;

                                        this.enableCreateButtonIfRequired();
                                    }
                                }
                            }}
                            selection={this._suiteSelectionMethod}
                            selectedText={this._selectedTestSuiteNameObservable}
                            noItemsText={Resources.NoSuites}
                            calloutContentClassName={""}
                        />
                    </FormItem>
                ) : (
                    <FormItem label={Resources.SelectSuite} className="test-suite-name">
                        <Dropdown
                            className="suite-dropdown-button"
                            ariaLabel={this._selectedSuiteName}
                            items={this._suiteList}
                            selection={this._suiteSelectionMethod}
                            loading={this._suitesLoading}
                            onSelect={(event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem<{}>) => {
                                if (item.text && item.text != this._selectedSuiteName) {
                                    this._selectedSuiteName = item.text;
                                    this._selectedSuiteId = item.id ? parseInt(item.id, 10) : -1;

                                    this.enableCreateButtonIfRequired();
                                }
                            }}
                            noItemsText={Resources.NoSuites}
                            calloutContentClassName={""}
                        />
                    </FormItem>
                )}

                <div className="clone-option-component">
                    <Checkbox
                        className="clone-option-checkbox"
                        id="include-links"
                        label={Resources.IncludeLinks}
                        checked={this._includeLinks}
                        onChange={(event: React.SyntheticEvent<HTMLElement>, checked: boolean) => {
                            this._includeLinks.value = checked;
                        }}
                        ariaLabel={Resources.IncludeLinks}
                    />
                    <div className="seperator"></div>
                    <Checkbox
                        className="clone-option-checkbox"
                        id="include-attachments"
                        label={Resources.IncludeAttachments}
                        checked={this._includeAttachments}
                        onChange={(event: React.SyntheticEvent<HTMLElement>, checked: boolean) => {
                            this._includeAttachments.value = checked;
                        }}
                        ariaLabel={Resources.IncludeAttachments}
                    />
                </div>
            </div>
        );

        return (
            <DynamicModuleLoader modules={[TestCasesClonePanelModule]}>
                <CustomPanel lightDismiss={false} onDismiss={() => (this.props.showPanel.value = false)}>
                    <CustomHeader>
                        <HeaderTitleArea>
                            <div className="clone-panel-header title-m">{Resources.CopyTestCases}</div>
                            <div className="clone-panel-subheading">{Resources.NewTestCases}</div>
                        </HeaderTitleArea>
                        <PanelCloseButton onDismiss={() => (this.props.showPanel.value = false)} />
                    </CustomHeader>
                    <PanelContent>
                        <div className="clone-test-cases-panel">{panelContent}</div>
                    </PanelContent>
                    <PanelFooter>
                        <div className="clone-panel-footer">
                            {this._isEditableDropdownEnabled ? (
                                <Button
                                    className="panel-create-button"
                                    text={Resources.Create}
                                    primary={true}
                                    disabled={this.props.cloneInProgress || !this.props.mandatoryFieldUpdated}
                                    onClick={() => {
                                        this.props.copyTestCases(
                                            this.props.sourcePlanId,
                                            this.props.sourcePlanName,
                                            this._SelectedTestPlanId,
                                            this._selectedTestPlanNameObservable.value,
                                            this.props.sourceSuiteId,
                                            this._selectedSuiteId,
                                            this._sourceProjectName,
                                            this._selectedProjectName,
                                            this.props.sourceTestCaseIds,
                                            this._includeLinks.value,
                                            this._includeAttachments.value
                                        );
                                    }}
                                />
                            ) : (
                                <Button
                                    className="panel-create-button"
                                    text={Resources.Create}
                                    primary={true}
                                    disabled={this.props.cloneInProgress || !this.props.mandatoryFieldUpdated}
                                    onClick={() => {
                                        this.props.copyTestCases(
                                            this.props.sourcePlanId,
                                            this.props.sourcePlanName,
                                            this._SelectedTestPlanId,
                                            this._selectedTestPlanName,
                                            this.props.sourceSuiteId,
                                            this._selectedSuiteId,
                                            this._sourceProjectName,
                                            this._selectedProjectName,
                                            this.props.sourceTestCaseIds,
                                            this._includeLinks.value,
                                            this._includeAttachments.value
                                        );
                                    }}
                                />
                            )}
                            {this.props.cloneInProgress && <Spinner className="spinner-footer" size={SpinnerSize.medium} />}
                        </div>
                    </PanelFooter>
                </CustomPanel>
            </DynamicModuleLoader>
        );
    }

    public componentDidMount(): void {
        this.props.disableTestSuiteEditableDropdownSelection(true);
    }

    public shouldComponentUpdate(nextProps: ITestCaseClonePanelViewProps) {
        // if opId is being created ie, the clone has been initiated, close  the panel
        if (nextProps.opId && nextProps.opId > 0) {
            this.props.showPanel.value = false;
        } else if (nextProps.opId == 0) {
            // If the clone operation failed hide the panel.
            this.props.showPanel.value = false;
        }

        if (nextProps.projectList && nextProps.projectList != this.props.projectList) {
            this._projectList.removeAll();

            nextProps.projectList.forEach((project: TeamProjectReference) => {
                this._projectList.push({
                    id: project.id,
                    text: project.name
                });
            });
            this._projectsLoading.value = false;
        }

        if (nextProps.testPlanList && nextProps.testPlanList != this._lastTestPlanListFetched) {
            if (!this._testPlanObjectList) {
                // Dont remove the first item from the list, which is the spinner component when the first batch of test plans is received
                this._testPlanList.splice(0, this._testPlanList.length - 1);
                this._testPlanObjectList = [];
            }

            // Clear the test plans loading spinner when all the test plans are loaded
            // If test plans list length < 200, that means there is no other pagination call left
            if (nextProps.testPlanList.length < 200) {
                this._testPlanList.splice(0, 1);
                this._testPlansLoading.value = false;
            }

            if (this._testPlanListFetchedForProject != nextProps.testPlanListProject) {
                this._testPlanListFetchedForProject = nextProps.testPlanListProject;
                this._testPlanObjectList = nextProps.testPlanList;
                // Dont remove the first item from the list, which is the spinner component when the project is changed and first batch of test plans is received
                this._testPlanList.splice(0, this._testPlanList.length - 1);
                this._suiteList.removeAll();
            } else {
                this._testPlanObjectList.concat(nextProps.testPlanList);
            }

            nextProps.testPlanList.forEach((testPlan: TestPlan) => {
                this._testPlanList.push({
                    id: testPlan.id.toString(),
                    text: testPlan.name
                });
            });

            this._lastTestPlanListFetched = nextProps.testPlanList;
        }

        if (nextProps.suiteList && this._suiteObjectList != nextProps.suiteList) {
            this._suiteList.removeAll();

            this._suiteObjectList = nextProps.suiteList;
            nextProps.suiteList.forEach((testSuite: TestSuite) => {
                this._addSuiteAndChildrenToList(testSuite, 1);
            });

            this._suitesLoading.value = false;
        }

        // Prefetch the projects list
        if (!this._projectListFetched) {
            this._projectListFetched = true;
            this._projectsLoading.value = true;
            this._projectList.push({ id: "loading", type: ListBoxItemType.Loading });
            this.props.getProjectsFromServer();
        }

        if (this._selectedProjectName != this._testPlanListFetchedForProject) {
            this._testPlanListFetchedForProject = this._selectedProjectName;
            this._testPlansLoading.value = true;
            this._isEditableDropdownEnabled && this._testPlanList.removeAll();
            this._testPlanList.push({ id: "loading", type: ListBoxItemType.Loading });
            this.props.getTestPlans(this._testPlanListFetchedForProject);
        }

        return true;
    }

    private _addSuiteAndChildrenToList(suite: TestSuite, depth: number) {
        if (suite && suite.suiteType == TestSuiteType.StaticTestSuite) {
            this._addSuiteWithDepth(suite, depth);

            if (suite.hasChildren && suite.children) {
                suite.children.forEach((testSuite: TestSuite) => {
                    this._addSuiteAndChildrenToList(testSuite, depth + 1);
                });
            }
        }
    }

    private _addSuiteWithDepth(suite: TestSuite, depth: number) {
        this._suiteList.push({
            id: suite.id.toString(),
            text: suite.name,
            className: "test-suite-margin-" + depth
        });
    }

    public enableCreateButtonIfRequired() {
        let update;
        if (this._isEditableDropdownEnabled) {
            update = this._selectedProjectName != "" && this._selectedTestPlanNameObservable.value != "" && this._selectedSuiteId != -1 ? true : false;
        } else {
            update = this._selectedProjectName != "" && this._selectedTestPlanName != "" && this._selectedSuiteId != -1 ? true : false;
        }
        if (update != this.props.mandatoryFieldUpdated) {
            this.props.updateMandatoryField(update);
        }
    }
}

function mapStateToProps(state: ITestCasesClonePanelAwareState): ITestCaseClonePanelViewProps {
    return {
        cloneInProgress: state.testCasesClonePanelState ? state.testCasesClonePanelState.cloneInProgress : false,
        opId: state.testCasesClonePanelState ? state.testCasesClonePanelState.opId : -1,
        mandatoryFieldUpdated: state.testCasesClonePanelState ? state.testCasesClonePanelState.mandatoryFieldUpdated : false,
        projectList: state.testCasesClonePanelState ? state.testCasesClonePanelState.projectList : [],
        testPlanList: state.testCasesClonePanelState ? state.testCasesClonePanelState.testPlanList : [],
        testPlanListProject: state.testCasesClonePanelState ? state.testCasesClonePanelState.testPlanListProject : "",
        suiteList: state.testCasesClonePanelState ? state.testCasesClonePanelState.suiteList : [],
        testPlanSelectionErrorMessage: state.testCasesClonePanelState ? state.testCasesClonePanelState.testPlanSelectionErrorMessage : "",
        testSuiteSelectionErrorMessage: state.testCasesClonePanelState ? state.testCasesClonePanelState.testSuiteSelectionErrorMessage : "",
        disableTestSuiteDropdown: state.testCasesClonePanelState ? state.testCasesClonePanelState.disableTestSuiteDropdown : false
    };
}

function mapDispatchToProps(dispatch: Dispatch): ITestCaseClonePanelViewDispatchProps {
    return {
        updateMandatoryField: (update: boolean) => dispatch(TestCasesClonePanelActions.mandatoryFieldUpdated(update)),
        getProjectsFromServer: () => dispatch(TestCasesClonePanelActions.getProjectsFromServer()),
        getTestPlans: (projectName: string) => dispatch(TestCasesClonePanelActions.getTestPlans(projectName)),
        getTestSuitesForPlan: (project: string, planId: number) => dispatch(TestCasesClonePanelActions.getTestSuitesForPlan(project, planId)),
        copyTestCases: (
            sourcePlanId: number,
            sourcePlanName: string,
            destinationPlanId: number,
            destinationPlanName: string,
            sourceSuiteId: number,
            destinationSuiteId: number,
            sourceProjectName: string,
            destinationProjectName: string,
            testCasesIds: number[],
            includeLinks: boolean,
            includeAttachments: boolean
        ) =>
            dispatch(
                TestCasesClonePanelActions.copyTestCases(
                    sourcePlanId,
                    sourcePlanName,
                    destinationPlanId,
                    destinationPlanName,
                    sourceSuiteId,
                    destinationSuiteId,
                    sourceProjectName,
                    destinationProjectName,
                    testCasesIds,
                    includeLinks,
                    includeAttachments
                )
            ),
        testCasesClonePanelError: (testPlanSelectionErrorMessage: string) =>
            dispatch(TestCasesClonePanelActions.testCasesClonePanelError(testPlanSelectionErrorMessage)),
        publishTelemetry: (action: string, payload: { [key: string]: any }) => dispatch(TelemetryActions.publishTelemetry(action, payload)),
        testCasesClonePanelSuiteSelectionError: (testSuiteSelectionErrorMessage: string) =>
            dispatch(TestCasesClonePanelActions.testCasesClonePanelSuiteSelectionError(testSuiteSelectionErrorMessage)),
        disableTestSuiteEditableDropdownSelection: (disableTestSuiteDropdown: boolean) => dispatch(TestCasesClonePanelActions.disableTestSuiteEditableDropdownSelection(disableTestSuiteDropdown))
    };
}

export function TestCasesClonePanelFunctionalComponent(
    props: ITestCaseClonePanelOwnProps & ITestCaseClonePanelViewProps & ITestCaseClonePanelViewDispatchProps
): JSX.Element {
    const testhubRefreshLocalContext = React.useContext(TestHubRefreshLocalContext);

    return <TestCasesClonePanelView {...props} testhubRefreshContext={testhubRefreshLocalContext} />;
}
export const TestCasesClonePanelConnectedView = connect(mapStateToProps, mapDispatchToProps)(TestCasesClonePanelFunctionalComponent);
Components.add("testcases-clone-panel", TestCasesClonePanelConnectedView);